import classNames from "classnames";
import * as React from "react";

import { Field } from "@/shared/foundation/primitives";

import styles from "./ParameterField.module.css";
import {
  ParameterFieldContext,
  useParameterField,
  type ParameterFieldParameter,
} from "./useParameterField";

export interface ParameterFieldRootProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  parameter: ParameterFieldParameter;
  /** What is wrong with the value; the row is invalid while this is set. */
  error?: string;
  /** Stamped as `data-set` on the row, for tests and styling. */
  isSet: boolean;
  children: React.ReactNode;
}

/**
 * The bordered row, stamped `data-testid="parameter-<name>"` and `data-set`;
 * any other prop (a `data-source`) rides through onto it. Its fields sit in a
 * `Field.Root`, so a Base UI control inside (Combobox, Select) picks up the
 * invalid state and aria wiring on its own.
 */
function Root({
  parameter,
  error,
  isSet,
  className,
  children,
  ...props
}: ParameterFieldRootProps) {
  const labelId = React.useId();
  const context = React.useMemo(
    () => ({ parameter, labelId, error }),
    [parameter, labelId, error],
  );
  return (
    <ParameterFieldContext.Provider value={context}>
      <div
        {...props}
        className={classNames(styles.row, className)}
        data-testid={`parameter-${parameter.name}`}
        data-set={isSet}
      >
        <Field.Root className={styles.fields} invalid={error !== undefined}>
          {children}
        </Field.Root>
      </div>
    </ParameterFieldContext.Provider>
  );
}

/** The parameter's name, which `labelId` points at, and whether it is required. */
function Header() {
  const { parameter, labelId } = useParameterField();
  return (
    <div className={styles.header}>
      <span id={labelId} className={styles.name}>
        {parameter.name}
      </span>
      <span className={styles.requirement}>
        {parameter.required ? "Required" : "Optional"}
      </span>
    </div>
  );
}

/** The parameter's description; nothing when it has none. */
function Description() {
  const { parameter } = useParameterField();
  return typeof parameter.description === "string" ? (
    <Field.Description>{parameter.description}</Field.Description>
  ) : null;
}

/** The area the controls sit in, side by side until the row is too narrow. */
function Inputs({ children }: { children: React.ReactNode }) {
  return <div className={styles.inputs}>{children}</div>;
}

/** The slot for the value control, taking the width the others leave. */
function Value({ children }: { children: React.ReactNode }) {
  return <div className={styles.value}>{children}</div>;
}

/** Root's error, as a `Field.Error` that always matches: zod decides, not the browser. */
function ErrorMessage() {
  const { error } = useParameterField();
  return <Field.Error match>{error}</Field.Error>;
}

/**
 * One declared parameter of an action, or variable of an agent, as a form row.
 * The shell only: each form puts its own controls inside `Inputs`.
 *
 * @example
 * ```tsx
 * <ParameterField.Root parameter={variable} error={error} isSet={isSet}>
 *   <ParameterField.Header />
 *   <ParameterField.Description />
 *   <ParameterField.Inputs>
 *     <ParameterField.Value>{control}</ParameterField.Value>
 *   </ParameterField.Inputs>
 *   <ParameterField.Error />
 * </ParameterField.Root>
 * ```
 */
export const ParameterField = Object.assign(Root, {
  Root,
  Header,
  Description,
  Inputs,
  Value,
  Error: ErrorMessage,
});
